import React from "react"
import { useLocation } from "react-router-dom"
import { useQuery } from "react-query"
import useSSR from "use-ssr"
import styled from "styled-components"
import debounce from "lodash.debounce"
import {
	Flex,
	HEADER_HEIGHT_L,
	HEADER_HEIGHT_S,
	breakpoint,
	scale,
	colors,
} from "@capioweb/ui-components/src"
import { DesktopHeader, DesktopHeaderXL, MobileHeader } from "./components"
import { getContent } from "../../api/content"
import { PUBLIC_API_ENDPOINT, Z_INDEX, queryStaleTime } from "../../constants"
import { UIContext } from "../../context"
import { MenuQueryProps, StickyHeaderProps } from "./types"
import { MyPagesMenu } from "./components/MyPagesMenu"
import { HeaderContextProvider } from "./context/HeaderContext"

/*
	- Sticky when scroll has passed header height
*/
const StickyHeader = styled.header<StickyHeaderProps>`
	position: ${({ sticky }) => (sticky ? "fixed" : "relative")};
	height: ${scale.px(HEADER_HEIGHT_S)};
	transition: height 0.5s ease;

	top: 0;
	left: 0;
	width: 100%;
	padding: 0 ${scale.px(3)};
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: ${colors.white};
	border-bottom: 1px solid ${colors.blue33};
	z-index: ${(props) => props.zIndex || 3};

	${breakpoint("l") <StickyHeaderProps>`
		height: ${(props) =>
			scale.px(props.sticky ? HEADER_HEIGHT_S : HEADER_HEIGHT_L)};
	`}
`

const Header: React.FC = (props) => {
	/* Menu query */
	const { data, isLoading } = useQuery<MenuQueryProps>(
		"menu",
		() => getContent(`${PUBLIC_API_ENDPOINT}/settings/menu/`),
		{
			staleTime: queryStaleTime,
		}
	)
	const location = useLocation()

	/* Sticky header and page title logic */
	const { isBrowser } = useSSR()
	const { pageAlertsHeight, pageAlerts } = React.useContext(UIContext)
	const [isStickyHeader, setIsStickyHeader] = React.useState(false)

	/* Listeners for sticky header && scroll direction */
	const scrollHandler = () => {
		const offset =
			pageAlerts?.common.length || pageAlerts?.local.length
				? pageAlertsHeight - 2
				: 0
		const scrollY = document.documentElement.scrollTop

		setIsStickyHeader(scrollY > offset * 8)
	}
	const debouncedScrollHandler = React.useCallback(
		debounce(scrollHandler, 10),
		[scrollHandler]
	)

	React.useEffect(() => {
		if (isBrowser) {
			window.addEventListener("scroll", debouncedScrollHandler, { passive: true })
			return () => {
				window.removeEventListener("scroll", debouncedScrollHandler)
			}
		}
	}, [isBrowser, debouncedScrollHandler])

	const showMyPagesMenu =
		data?.isLoggedIn &&
		data?.myPagesMenu &&
		data?.myPagesRoot &&
		location.pathname.indexOf(data?.myPagesRoot) > -1

	return (
		<HeaderContextProvider isStickyHeader={isStickyHeader}>
			<Flex w={1} mt={isStickyHeader ? HEADER_HEIGHT_S : 0}></Flex>
			<StickyHeader sticky={isStickyHeader} zIndex={Z_INDEX.Header}>
				{!isLoading && data && (
					<>
						<DesktopHeaderXL {...data} />
						<DesktopHeader {...data} />
						<MobileHeader {...data} />
					</>
				)}
			</StickyHeader>
			{!isLoading && showMyPagesMenu && (
				<MyPagesMenu
					menu={data.myPagesMenu}
					myPagesAccount={data.myPagesAccount}
					myPagesLogout={data.myPagesLogout}
				/>
			)}
		</HeaderContextProvider>
	)
}

export { Header }
